import { createTheme, alpha } from '@mui/material/styles'
import Grow from '@mui/material/Grow'
import type {} from '@mui/x-date-pickers/themeAugmentation'

/**
 * Theme MUI ← tokens da marca TransGrãos (skill/clean_corporate_style + BRAND.md).
 * MUI vem com radius arredondado, cinza-pedra e Roboto — os três violam a marca;
 * aqui mapeamos acento floresta, neutros "sage", Fredoka/Nunito e o raio-folha.
 *
 * Dois modos: `createAppTheme('light' | 'dark')`. O claro é o oficial da marca;
 * o escuro reusa o acento floresta (erguido p/ contraste) sobre neutros sage-escuros.
 * Os neutros ficam num único objeto por modo (`sageLight`/`sageDark`) e alimentam
 * tanto o palette quanto os component overrides — trocar de modo troca a fonte.
 */

// ── Cores da marca (espelham tokens.css) ─────────────────────────────
export const brand = {
  floresta: '#00552C',
  florestaDeep: '#003D1F',
  florestaSoft: '#0B6E38',
  florestaLight: '#17864A',
  folha: '#799A00',
  folhaDeep: '#5C7500',
  lime: '#C6D800',
  branco: '#FFFFFF',
  gradient: 'linear-gradient(158deg, #003D1F 0%, #00552C 52%, #0B6E38 100%)',
} as const

// Neutros do modo claro — paleta "sage" (off-white esverdeado).
const sageLight = {
  appBg: '#F6F8EF',
  surface: '#FFFFFF',
  surfaceStrong: '#EDF1E1',
  text: '#2B3720',
  textStrong: '#1B2412',
  textMuted: '#6D7A5C',
  textSubtle: '#9AA889',
  border: 'rgba(60, 74, 40, 0.16)',
  borderStrong: 'rgba(60, 74, 40, 0.26)',
  divider: 'rgba(60, 74, 40, 0.10)',
  hover: '#EEF2E2',
  active: '#E2E8D4',
} as const

// Neutros do modo escuro — "sage" rebaixado (quase-preto esverdeado + tintas sage claras).
const sageDark = {
  appBg: '#10140C',
  surface: '#181D12',
  surfaceStrong: '#222819',
  text: '#E7ECDD',
  textStrong: '#F4F7EE',
  textMuted: '#A3B18C',
  textSubtle: '#74815F',
  border: 'rgba(200, 216, 170, 0.14)',
  borderStrong: 'rgba(200, 216, 170, 0.24)',
  divider: 'rgba(200, 216, 170, 0.09)',
  hover: '#1F2617',
  active: '#28311D',
} as const

/** Compat: nada mais importa `sage`, mas mantemos o alias apontando p/ o claro. */
export const sage = sageLight

/** Assinatura TransGrãos — raio-folha (cantos opostos TR + BL). */
export const LEAF_RADIUS = '0 28px 0 28px'
export const LEAF_RADIUS_SM = '0 16px 0 16px'
/** Easing "momentos-folha" (espelha --ease-organic de tokens.css). */
export const EASE_ORGANIC = 'cubic-bezier(0.45, 0, 0.35, 1)'

const FONT_SANS = '"Nunito Sans", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'
const FONT_DISPLAY = '"Fredoka", ' + FONT_SANS

// ── Augmentação: cores decorativas leaf/lime no palette ──────────────
declare module '@mui/material/styles' {
  interface Palette {
    leaf: Palette['primary']
    lime: Palette['primary']
  }
  interface PaletteOptions {
    leaf?: PaletteOptions['primary']
    lime?: PaletteOptions['primary']
  }
}

export type AppThemeMode = 'light' | 'dark'

/** Cores semânticas de situação. `light` é TINTA DE FUNDO, translúcida de
 *  propósito, e por isso nunca serve de cor de texto: o Alert do MUI deriva as
 *  duas da mesma entrada, e foi assim que todo aviso do sistema saiu lavado. */
const SEMANTICAS = {
  success: { main: '#16a34a', dark: '#166534', light: 'rgba(22,163,74,0.12)' },
  warning: { main: '#d97706', dark: '#92400e', light: 'rgba(217,119,6,0.12)' },
  error: { main: '#dc2626', dark: '#991b1b', light: 'rgba(220,38,38,0.12)' },
  info: { main: '#0891b2', dark: '#155e75', light: 'rgba(8,145,178,0.12)' },
} as const

/** Constrói o theme MUI para o modo pedido. Trocar de modo re-deriva tudo. */
export function createAppTheme(mode: AppThemeMode) {
  const isDark = mode === 'dark'
  const n = isDark ? sageDark : sageLight

  // Acento floresta — no escuro sobe para manter contraste sobre fundo escuro.
  const accent = isDark ? '#1F9E56' : brand.floresta
  const accentDark = isDark ? '#17864A' : brand.florestaDeep
  const accentLight = isDark ? '#46C97E' : brand.florestaLight
  // Hover do primário: claro escurece (floresta-deep); escuro clareia.
  const accentHover = isDark ? '#28B267' : brand.florestaDeep
  // Verde legível sobre fundo (links, texto selecionado, hover de borda).
  const accentReadable = isDark ? accentLight : brand.floresta
  const inputBorderHover = isDark ? accentLight : brand.florestaSoft

  const selectedFill = alpha(accentReadable, isDark ? 0.18 : 0.12)
  const focusRing = `0 0 0 3px ${alpha(accentReadable, isDark ? 0.35 : 0.2)}`
  const selection = alpha(accentReadable, isDark ? 0.3 : 0.16)

  const shadowCard = isDark
    ? '0 1px 2px 0 rgba(0,0,0,0.5), 0 4px 16px 0 rgba(0,0,0,0.35)'
    : '0 1px 2px 0 rgba(20,40,18,0), 0 4px 16px 0 rgba(20,40,18,0.04)'
  const shadowDropdown = isDark ? '0 15px 35px rgba(0,0,0,0.55)' : '0 15px 35px rgba(20,40,18,0.12)'

  return createTheme({
    cssVariables: false,
    palette: {
      mode,
      primary: {
        main: accent,
        dark: accentDark,
        light: accentLight,
        contrastText: brand.branco,
      },
      secondary: {
        main: brand.folha,
        dark: brand.folhaDeep,
        light: brand.lime,
        contrastText: brand.branco,
      },
      leaf: { main: brand.folha, dark: brand.folhaDeep, light: brand.lime, contrastText: brand.branco },
      lime: { main: brand.lime, contrastText: brand.florestaDeep },
      success: SEMANTICAS.success,
      warning: SEMANTICAS.warning,
      error: SEMANTICAS.error,
      info: SEMANTICAS.info,
      background: { default: n.appBg, paper: n.surface },
      text: { primary: n.text, secondary: n.textMuted, disabled: n.textSubtle },
      divider: n.border,
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: FONT_SANS,
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 600,
      h1: { fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: '2.25rem', lineHeight: 1.2, letterSpacing: '-0.02em' },
      h2: { fontFamily: FONT_DISPLAY, fontWeight: 500, fontSize: '1.875rem', lineHeight: 1.2, letterSpacing: '-0.01em' },
      h3: { fontFamily: FONT_DISPLAY, fontWeight: 500, fontSize: '1.5rem', lineHeight: 1.2, letterSpacing: '-0.01em' },
      h4: { fontFamily: FONT_DISPLAY, fontWeight: 500, fontSize: '1.25rem', lineHeight: 1.25 },
      h5: { fontFamily: FONT_DISPLAY, fontWeight: 500, fontSize: '1.0625rem', lineHeight: 1.35 },
      h6: { fontFamily: FONT_DISPLAY, fontWeight: 500, fontSize: '0.9375rem', lineHeight: 1.35 },
      subtitle1: { fontSize: '0.9375rem', fontWeight: 500 },
      subtitle2: { fontSize: '0.8125rem', fontWeight: 600, color: n.textMuted },
      body1: { fontSize: '0.875rem', lineHeight: 1.5 },
      body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
      button: { fontFamily: FONT_DISPLAY, fontWeight: 500, fontSize: '0.9375rem', textTransform: 'none' },
      caption: { fontSize: '0.75rem', color: n.textMuted },
      overline: { fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: n.textMuted },
    },
    components: {
      // O Alert do MUI deriva TEXTO e FUNDO da MESMA entrada `light` da paleta.
      // Aqui `light` é um rgba com 12% de opacidade, escolhido para servir de
      // tinta de fundo, e o texto herdava essa transparência: todo aviso do
      // sistema saía lavado, quase ilegível sobre o cartão claro.
      //
      // A correção separa as duas pontas. O fundo continua sendo a tinta
      // translúcida, que é o efeito desejado; o texto e o ícone passam a vir de
      // cores opacas. No tema claro usa-se o tom escuro da severidade; no
      // escuro, o tom principal, que é o que mantém contraste sobre o fundo
      // profundo sem virar borrão.
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: LEAF_RADIUS_SM },
          ...Object.fromEntries(
            (['success', 'warning', 'error', 'info'] as const).map((severidade) => [
              `standard${severidade[0].toUpperCase()}${severidade.slice(1)}`,
              {
                backgroundColor: SEMANTICAS[severidade].light,
                color: isDark ? SEMANTICAS[severidade].main : SEMANTICAS[severidade].dark,
                '& .MuiAlert-icon': { color: SEMANTICAS[severidade].main },
              },
            ]),
          ),
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: n.appBg,
            color: n.text,
            WebkitFontSmoothing: 'antialiased',
            textRendering: 'optimizeLegibility',
          },
          '::selection': { background: selection },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 10,
            fontWeight: 500,
            paddingInline: 18,
            minHeight: 40,
            transition: 'transform 140ms ease-out, background-color 150ms ease, box-shadow 150ms ease',
            '&.Mui-focusVisible': { boxShadow: focusRing },
            // Press: feedback tátil (Emil). Botão ≠ card — aqui scale é permitido.
            '&:active': { transform: 'scale(0.97)' },
            '@media (prefers-reduced-motion: reduce)': { '&:active': { transform: 'none' } },
          },
          // Assinatura: TODO botão de destaque (contained/outlined, qualquer cor) veste
          // o raio-folha — como o "Entrar" do login. Botões pequenos usam o raio-folha
          // reduzido (o seletor com 2 classes vence o do variant por especificidade).
          contained: {
            borderRadius: LEAF_RADIUS,
            '&.MuiButton-sizeSmall': { borderRadius: LEAF_RADIUS_SM },
          },
          outlined: {
            borderRadius: LEAF_RADIUS,
            '&.MuiButton-sizeSmall': { borderRadius: LEAF_RADIUS_SM },
          },
          // Ação primária: mantém o hover floresta (claro escurece; escuro clareia).
          containedPrimary: {
            '&:hover': { backgroundColor: accentHover },
          },
        },
      },
      // Filtros / segmented controls com forma de folha (Cargas, Contratos, Usuários).
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            backgroundColor: n.surface,
            border: `1px solid ${n.border}`,
            borderRadius: LEAF_RADIUS_SM,
            overflow: 'hidden',
            padding: 0,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            position: 'relative',
            zIndex: 0,
            overflow: 'hidden',
            border: 0,
            borderRadius: 0,
            fontFamily: FONT_DISPLAY,
            fontWeight: 500,
            textTransform: 'none',
            color: n.textMuted,
            paddingInline: 16,
            minHeight: 38,
            transition: `color 180ms ${EASE_ORGANIC}, transform 140ms ease-out`,
            // Folha desabrochando: preenchimento verde revelado por clip-path (esquerda → direita).
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              zIndex: -1,
              backgroundColor: selectedFill,
              clipPath: 'inset(0 100% 0 0)',
              transition: `clip-path 260ms ${EASE_ORGANIC}`,
            },
            '&:hover': { backgroundColor: n.hover, color: n.text },
            '&.Mui-selected': {
              color: accentReadable,
              fontWeight: 600,
              backgroundColor: 'transparent',
              '&:hover': { backgroundColor: 'transparent' },
              '&::before': { clipPath: 'inset(0 0 0 0)' },
            },
            '&:active': { transform: 'scale(0.97)' },
            // Anel interno (o overflow-hidden do grupo-folha recortaria um anel externo).
            '&.Mui-focusVisible': { boxShadow: `inset 0 0 0 2px ${alpha(accentReadable, 0.55)}`, zIndex: 1 },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: { borderRadius: 16 },
          elevation1: { boxShadow: shadowCard },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 1 },
        styleOverrides: {
          root: { borderRadius: 16, boxShadow: shadowCard, backgroundImage: 'none' },
        },
      },
      MuiMenu: {
        styleOverrides: { paper: { borderRadius: LEAF_RADIUS_SM, boxShadow: shadowDropdown, overflow: 'hidden' } },
      },
      // Popup do Autocomplete/Select na mesma forma-folha dos date pickers (dropdowns coerentes).
      MuiAutocomplete: {
        styleOverrides: { paper: { borderRadius: LEAF_RADIUS_SM, boxShadow: shadowDropdown } },
      },
      MuiPopover: {
        styleOverrides: { paper: { borderRadius: LEAF_RADIUS_SM } },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: LEAF_RADIUS_SM,
            backgroundColor: n.surface,
            '&.MuiInputBase-sizeSmall:not(.MuiInputBase-multiline)': { height: 40 },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: n.borderStrong },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: inputBorderHover },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: accent, borderWidth: 1.5 },
            '&.Mui-focused': { boxShadow: focusRing },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: n.divider },
          head: { fontWeight: 600, color: n.textMuted, fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase' },
        },
      },
      MuiTooltip: {
        // Padrão do sistema: card branco em forma de folha, fonte um pouco maior e
        // micro-animação (Grow) ao aparecer e sumir. Mesma linguagem dos popovers.
        defaultProps: {
          TransitionComponent: Grow,
          TransitionProps: { timeout: { enter: 180, exit: 120 } },
          enterDelay: 150,
        },
        styleOverrides: {
          tooltip: {
            backgroundColor: n.surface,
            color: n.text,
            fontSize: '0.8125rem',
            fontWeight: 500,
            lineHeight: 1.45,
            borderRadius: LEAF_RADIUS_SM,
            border: `1px solid ${n.border}`,
            boxShadow: shadowDropdown,
            padding: '8px 12px',
            maxWidth: 320,
          },
          arrow: {
            color: n.surface,
            '&::before': { border: `1px solid ${n.border}` },
          },
        },
      },
      MuiLink: {
        defaultProps: { underline: 'hover' },
        styleOverrides: { root: { color: accentReadable, fontWeight: 600 } },
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: n.divider } },
      },
      // Date/time pickers (@mui/x-date-pickers) — popup na forma de folha.
      MuiPickersPopper: {
        styleOverrides: {
          paper: { borderRadius: LEAF_RADIUS, boxShadow: shadowDropdown, overflow: 'hidden' },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            '&.Mui-selected': {
              backgroundColor: accent,
              color: brand.branco,
              '&:hover, &:focus': { backgroundColor: accentHover },
            },
            '&.MuiPickersDay-today': { borderColor: accent },
          },
        },
      },
    },
  })
}

/** Theme padrão (claro) — mantido p/ compat com imports diretos. */
export const theme = createAppTheme('light')

export const layout = {
  sidebarWidth: 248,
  sidebarCompact: 64,
  topbarHeight: 64,
  /** Altura da lista rolável das linhas do tempo (histórico de status do conjunto,
   *  da viagem e do veículo). O histórico cresce a cada mudança, e sem teto o
   *  diálogo tomava a tela inteira e empurrava o botão de fechar para fora. Com
   *  teto, título e ação ficam sempre visíveis e só a lista rola. */
  historicoListHeight: 380,
  /** Teto da lista rolável dos rankings do Painel de Jornada. Os cards passaram a
   *  carregar a frota inteira (Samuel, 13/08/2026) e a página não pode crescer com
   *  ela: é a altura que os dez primeiros já ocupavam, então a faixa de cards
   *  continua com a mesma estatura e a cauda vem pelo scroll. */
  rankingListHeight: 304,
} as const

export const palette = { brand, sage: sageLight } as const
